import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import client from "@/api/client";
import {
  Loader2,
  RefreshCw,
  History,
  Download,
  CalendarIcon,
  X,
  Search,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Filter,
  UserCog,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import { ar as arLocale } from "date-fns/locale";

const PAGE_SIZE = 25;

const ACTION_TONE = [
  { match: /(deleted|rejected|removed|revoked)/, className: "bg-rose-500/10 text-rose-600 border-rose-500/25" },
  { match: /(approved|created|added|granted|installed)/, className: "bg-emerald-500/10 text-emerald-700 border-emerald-500/25" },
  { match: /(updated|renamed|moved|reset)/, className: "bg-amber-500/10 text-amber-700 border-amber-500/25" },
];

function toneFor(action) {
  const hit = ACTION_TONE.find((r) => r.match.test(action || ""));
  return hit ? hit.className : "bg-primary/10 text-primary border-primary/25";
}

/** Localize `entity.verb` action ids, falling back to a humanized raw value. */
function makeActionLabel(tr) {
  return (action) => {
    const raw = String(action || "");
    const idx = raw.lastIndexOf(".");
    if (idx <= 0) return raw.replace(/[._]/g, " ");
    const entity = raw.slice(0, idx);
    const verb = raw.slice(idx + 1);
    const entityLabel = tr(`auditEntities.${entity}`, { defaultValue: entity.replace(/_/g, " ") });
    const verbLabel = tr(`auditVerbs.${verb}`, { defaultValue: verb.replace(/_/g, " ") });
    return `${entityLabel} · ${verbLabel}`;
  };
}

function shortValue(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "on" : "off";
  const s = String(v);
  return s.length > 46 ? `${s.slice(0, 46)}…` : s;
}

function when(iso, locale) {
  try {
    const d = parseISO(iso);
    return {
      rel: formatDistanceToNow(d, { addSuffix: true, locale }),
      abs: format(d, "MMM d, yyyy · HH:mm", { locale }),
    };
  } catch {
    return { rel: "", abs: iso || "" };
  }
}

function ChangeChips({ changes }) {
  if (!changes || changes.length === 0) {
    return <span className="text-xs text-muted-foreground/70">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {changes.slice(0, 6).map((c, i) => (
        <span
          key={`${c.field}-${i}`}
          className="inline-flex items-center gap-1 rounded-lg border border-border bg-muted/50 px-2 py-0.5 text-[11px] font-mono"
        >
          <span className="text-muted-foreground">{c.field}</span>
          <span className="line-through opacity-60">{shortValue(c.from)}</span>
          <ArrowRight className="h-3 w-3 text-primary rtl-flip" />
          <span className="font-semibold">{shortValue(c.to)}</span>
        </span>
      ))}
      {changes.length > 6 && (
        <span className="text-[11px] text-muted-foreground">+{changes.length - 6}</span>
      )}
    </div>
  );
}

/**
 * AdminAuditLog — "who changed what" across the platform, with field-level
 * before/after diffs, filters and CSV export.
 */
export function AdminAuditLog() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const dfLocale = isAr ? arLocale : undefined;
  const prettyAction = useMemo(() => makeActionLabel(t), [t]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [facets, setFacets] = useState({ actions: [], entity_types: [], actors: [] });
  const [q, setQ] = useState("");
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const [entityType, setEntityType] = useState("");
  const [changedOnly, setChangedOnly] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const params = useMemo(() => {
    const p = { limit: PAGE_SIZE, skip };
    if (q.trim()) p.q = q.trim();
    if (actor) p.actor = actor;
    if (action) p.action = action;
    if (entityType) p.entity_type = entityType;
    if (changedOnly) p.changed_only = 1;
    if (startDate) p.start_date = format(startDate, "yyyy-MM-dd");
    if (endDate) p.end_date = format(endDate, "yyyy-MM-dd");
    return p;
  }, [skip, q, actor, action, entityType, changedOnly, startDate, endDate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/admin/audit-logs", { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("adminActivity.loadError", { defaultValue: "Failed to load activity" }));
    } finally {
      setLoading(false);
    }
  }, [params, t]);

  useEffect(() => {
    const id = setTimeout(load, 200);
    return () => clearTimeout(id);
  }, [load]);

  useEffect(() => {
    client.get("/admin/audit-logs/facets")
      .then((res) => setFacets(res.data || { actions: [], entity_types: [], actors: [] }))
      .catch(() => {});
  }, []);

  const exportCsv = async () => {
    try {
      const { limit, skip: _s, ...rest } = params;
      const res = await client.get("/admin/audit-logs/export", { params: rest, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-log-${format(new Date(), "yyyyMMdd-HHmm")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t("adminActivity.exportError", { defaultValue: "Export failed" }));
    }
  };

  const clearFilters = () => {
    setQ(""); setActor(""); setAction(""); setEntityType("");
    setChangedOnly(false); setStartDate(null); setEndDate(null); setSkip(0);
  };
  const hasFilters = !!(q || actor || action || entityType || changedOnly || startDate || endDate);

  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4" data-testid="admin-audit-log-panel">
      <Card className="rounded-2xl border border-border p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <History className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <h2 className="font-display text-lg sm:text-xl font-semibold truncate">
                {t("adminActivity.title", { defaultValue: "Change log" })}
              </h2>
              <p className="text-xs sm:text-sm text-muted-foreground">
                {t("adminActivity.subtitle", { defaultValue: "Every create, update and delete across the platform — with before/after values." })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-xl gap-2 flex-1 sm:flex-none" onClick={exportCsv} data-testid="admin-audit-export">
              <Download className="h-4 w-4" />
              <span className="text-xs">{t("adminActivity.export", { defaultValue: "Export CSV" })}</span>
            </Button>
            <Button variant="outline" size="sm" className="rounded-xl gap-2" onClick={() => { setSkip(0); load(); }} disabled={loading} data-testid="admin-audit-refresh">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="rounded-2xl border border-border p-3 sm:p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2.5">
          <div className="relative">
            <Search className="h-4 w-4 text-muted-foreground absolute start-3 top-1/2 -translate-y-1/2" />
            <Input
              value={q}
              onChange={(e) => { setQ(e.target.value); setSkip(0); }}
              placeholder={t("adminActivity.searchPlaceholder", { defaultValue: "Search actor, entity or action" })}
              className="rounded-xl ps-9 h-10"
              data-testid="admin-audit-search"
            />
          </div>
          <Select value={actor || "_all"} onValueChange={(v) => { setActor(v === "_all" ? "" : v); setSkip(0); }}>
            <SelectTrigger className="rounded-xl h-10" data-testid="admin-audit-actor-filter">
              <SelectValue placeholder={t("adminActivity.allActors", { defaultValue: "All actors" })} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">{t("adminActivity.allActors", { defaultValue: "All actors" })}</SelectItem>
              {facets.actors.map((a) => (
                <SelectItem key={a.user_id} value={a.user_id}>{a.name} · {a.count}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={entityType || "_all"} onValueChange={(v) => { setEntityType(v === "_all" ? "" : v); setSkip(0); }}>
            <SelectTrigger className="rounded-xl h-10" data-testid="admin-audit-entity-filter">
              <SelectValue placeholder={t("adminActivity.allEntities", { defaultValue: "All areas" })} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">{t("adminActivity.allEntities", { defaultValue: "All areas" })}</SelectItem>
              {facets.entity_types.map((e) => (
                <SelectItem key={e} value={e}>{t(`auditEntities.${e}`, { defaultValue: e.replace(/_/g, " ") })}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={action || "_all"} onValueChange={(v) => { setAction(v === "_all" ? "" : v); setSkip(0); }}>
            <SelectTrigger className="rounded-xl h-10" data-testid="admin-audit-action-filter">
              <SelectValue placeholder={t("adminActivity.allActions", { defaultValue: "All actions" })} />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="_all">{t("adminActivity.allActions", { defaultValue: "All actions" })}</SelectItem>
              {facets.actions.map((a) => (
                <SelectItem key={a} value={a}>{prettyAction(a)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="rounded-lg h-9 gap-1.5 font-normal" data-testid="admin-audit-start-date">
                <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                {startDate ? format(startDate, "MMM d, yyyy") : t("access_log.from", { defaultValue: "From" })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={startDate} onSelect={(d) => { setStartDate(d || null); setSkip(0); }} initialFocus />
            </PopoverContent>
          </Popover>
          <span className="text-xs text-muted-foreground">→</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="rounded-lg h-9 gap-1.5 font-normal" data-testid="admin-audit-end-date">
                <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                {endDate ? format(endDate, "MMM d, yyyy") : t("access_log.to", { defaultValue: "To" })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={endDate} onSelect={(d) => { setEndDate(d || null); setSkip(0); }} disabled={startDate ? { before: startDate } : undefined} initialFocus />
            </PopoverContent>
          </Popover>
          <Button
            variant={changedOnly ? "default" : "outline"}
            size="sm"
            className="rounded-lg h-9 gap-1.5 text-xs"
            onClick={() => { setChangedOnly((v) => !v); setSkip(0); }}
            data-testid="admin-audit-changed-only"
          >
            <Filter className="h-3.5 w-3.5" />
            {t("adminActivity.changedOnly", { defaultValue: "With field changes" })}
          </Button>
          {hasFilters && (
            <Button variant="ghost" size="sm" className="rounded-lg h-9 gap-1 text-xs text-muted-foreground" onClick={clearFilters} data-testid="admin-audit-clear">
              <X className="h-3.5 w-3.5" />
              {t("access_log.clearDates", { defaultValue: "Clear" })}
            </Button>
          )}
          <span className="ms-auto text-xs text-muted-foreground tabular-nums" data-testid="admin-audit-total">
            {loading ? "—" : total} {t("adminActivity.entries", { defaultValue: "entries" })}
          </span>
        </div>
      </Card>

      {loading && items.length === 0 ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <Card className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="admin-audit-empty">
          <History className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            {t("adminActivity.empty", { defaultValue: "No recorded changes match these filters yet." })}
          </p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="admin-audit-list">
          {items.map((row) => {
            const w = when(row.created_at, dfLocale);
            return (
              <Card
                key={row.audit_id}
                className="rounded-2xl border border-border p-3.5 sm:p-4 transition-colors hover:border-primary/40"
                data-testid={`admin-audit-row-${row.audit_id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className={`text-[10px] uppercase tracking-widest ${toneFor(row.action)}`}>
                        {prettyAction(row.action)}
                      </Badge>
                      {row.source === "auto" && (
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground/70">
                          {t("adminActivity.auto", { defaultValue: "auto" })}
                        </span>
                      )}
                      <span className="text-sm font-medium truncate max-w-full">
                        {row.entity_label || row.entity_id || row.entity_type}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
                      <UserCog className="h-3 w-3" />
                      <span className="font-medium text-foreground/80">{row.actor_name || row.actor_email || "system"}</span>
                      <span>·</span>
                      <span>{t(`auditEntities.${row.entity_type}`, { defaultValue: (row.entity_type || "").replace(/_/g, " ") })}</span>
                      {row.ip && <><span>·</span><code className="font-mono">{row.ip}</code></>}
                    </p>
                    <div className="mt-2"><ChangeChips changes={row.changes} /></div>
                  </div>
                  <div className="sm:text-end shrink-0">
                    <p className="text-[11px] font-medium">{w.rel}</p>
                    <p className="text-[10px] text-muted-foreground font-mono">{w.abs}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-3 pt-1">
          <p className="text-xs text-muted-foreground tabular-nums">
            {skip + 1}–{Math.min(total, skip + PAGE_SIZE)} / {total}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-lg h-9" onClick={() => setSkip((s) => Math.max(0, s - PAGE_SIZE))} disabled={skip === 0 || loading} data-testid="admin-audit-prev">
              {isAr ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </Button>
            <span className="text-xs text-muted-foreground tabular-nums">{currentPage} / {totalPages}</span>
            <Button variant="outline" size="sm" className="rounded-lg h-9" onClick={() => setSkip((s) => s + PAGE_SIZE)} disabled={skip + PAGE_SIZE >= total || loading} data-testid="admin-audit-next">
              {isAr ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminAuditLog;
